import { useSyncExternalStore } from "react";
import { invalidate } from "./invalidate.js";

// Tracks whether the backend is currently reachable, so the UI can tell the
// user when the data on screen is stale (served from useQuery.ts's
// stale-while-revalidate cache) because the API can't be reached — instead of
// silently presenting last-known data as if it were live.
//
// This is a plain framework-free store (mirrors i18n/store.ts and
// saveStatus.ts): api/client.ts reports every request outcome in via
// reportReachable()/reportUnreachable(), React reads it through
// useConnectivity(), and the classification of *which* failures mean
// "unreachable" lives in client.ts (the one module that knows the error
// taxonomy) rather than here.

export type ConnectivityStatus = "online" | "offline";

// Flip to offline only after this many consecutive reachability failures, so a
// single transient blip (one dropped request that immediately recovers)
// doesn't flash the banner. Any success resets the counter.
const OFFLINE_THRESHOLD = 2;

let status: ConnectivityStatus = "online";
let consecutiveFailures = 0;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function setStatus(next: ConnectivityStatus): void {
  if (next === status) return;
  const wasOffline = status === "offline";
  status = next;
  notify();
  // On recovery, refresh every mounted query immediately (the query cache
  // subscribes to invalidation) so stale data is replaced with fresh data the
  // moment the API returns, rather than waiting for each query's next natural
  // revalidation.
  if (wasOffline && next === "online") invalidate();
}

/** Report that the backend responded (a success, or any HTTP status — even a
 * 4xx — since that still proves the server is reachable). Clears the failure
 * streak and marks the app online. */
export function reportReachable(): void {
  consecutiveFailures = 0;
  setStatus("online");
}

/** Report that a request could not reach the backend (network failure, 5xx, or
 * the API's own "unexpected" 500). Marks the app offline once the streak
 * crosses the threshold. */
export function reportUnreachable(): void {
  consecutiveFailures += 1;
  if (consecutiveFailures >= OFFLINE_THRESHOLD) setStatus("offline");
}

export function getConnectivityStatus(): ConnectivityStatus {
  return status;
}

export function subscribeConnectivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// The browser's own network state is authoritative: a real `offline` event
// means no request can possibly succeed, so drop straight to offline without
// waiting for the failure threshold. We do NOT force `online` on the `online`
// event — the network returning doesn't prove the API is back (it may still be
// cold-starting); the next successful request flips it via reportReachable().
if (typeof window !== "undefined") {
  window.addEventListener("offline", () => setStatus("offline"));
  window.addEventListener("online", () => {
    consecutiveFailures = 0;
  });
}

/** Subscribe to backend reachability, re-rendering on change. */
export function useConnectivity(): ConnectivityStatus {
  return useSyncExternalStore(subscribeConnectivity, getConnectivityStatus, getConnectivityStatus);
}
